import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import { Dot } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { ImageEscrow } from 'src/components/Svg/Images';
import { cn } from 'src/lib/utils';
import { EscrowStatusEnum } from 'src/graphql/schema';

type Props = {
  className?: string;
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <TableRow>
    <TableCell className="px-1 w-[116px] font-bold">{label}</TableCell>
    <TableCell className="w-9">:</TableCell>
    <TableCell>{value}</TableCell>
  </TableRow>
);

const Basics = ({ className }: Props) => {
  const {
    softwareEscrow: {
      status,
      startedBy,
      createdAt,
      signedAt,
      activatedAt,
      userUpdatedAt,
      agreement,
      [startedBy]: creator,
    },
  } = useSoftwareEscrow();

  const rows1 = [
    {
      label: 'Created by',
      value: creator?.companyName || '',
    },
    {
      label: 'Creator role',
      value: capitalize(startedBy),
    },
    {
      label: 'Created on',
      value: dayjs(createdAt).format('MMM DD, YYYY'),
    },
  ];

  const rows2 = [
    {
      label: 'Signed on',
      value: signedAt ? dayjs(signedAt).format('MMM DD, YYYY') : '',
    },
    {
      label: 'Activated on',
      value: activatedAt ? dayjs(activatedAt).format('MMM DD, YYYY') : '',
    },
    {
      label: 'Last update',
      value: userUpdatedAt ? dayjs(userUpdatedAt).format('MMM DD, YYYY') : '',
    },
  ];

  const isTerminated = status === EscrowStatusEnum.Terminated;

  return (
    <Card className={cn('shrink-0', isTerminated && 'opacity-70 pointer-events-none', className)}>
      <CardHeader className="space-y-3 pb-3">
        <CardTitle className="text-lg">Basics</CardTitle>
        <div className="flex items-center gap-1.5 text-xs font-bold">
          <div className="flex items-center gap-2">
            <ImageEscrow className="size-4" />
            Software Escrow
          </div>
          <Dot className="size-6" />
          {capitalize(agreement.agreementForm || '')}
        </div>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-x-6">
        <Table className="border-b md:border-b-0">
          <TableBody>
            {rows1.map(({ label, value }) => (
              <InfoRow key={label} label={label} value={value} />
            ))}
          </TableBody>
        </Table>
        <Table>
          <TableBody>
            {rows2.map(({ label, value }) => (
              <InfoRow key={label} label={label} value={value} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default Basics;
